import { DeleteOutlined, FileOutlined } from '@ant-design/icons';
import { DocumentPreviewPane, SupplierSelect } from '@components';
import { useDocumentSasUrl, useView } from '@hooks';
import { L } from '@lib';
import { Inventory } from '@services';
import { Button, Col, DatePicker, Drawer, Form, FormInstance, Image, Input, InputNumber, Popconfirm, Row, Segmented, Select, Space } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

const dayjsRef = (v: any) => (v ? dayjs(v) : undefined);
import { ItemInvoiceAttachment } from '../ItemInvoiceAttachment';

type PreviewDoc = { name: string; src: string; contentType: string };

interface IProps {
  open: boolean;
  editingId: string | null;
  editingDocuments: Inventory['documents'];
  form: FormInstance;
  categoryOptions: { label: string; value: string }[];
  itemInvoiceFile: { base64: string; name: string; contentType: string } | null;
  previewDoc: PreviewDoc | null;
  saving: boolean;
  deleting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onItemInvoiceSelect: (file: File) => void;
  onItemInvoiceRemove: () => void;
  onPreviewDocument: (doc: PreviewDoc) => void;
  onClosePreview: () => void;
  onDelete?: () => void | Promise<void>;
}

const MarginDisplay: React.FC = () => {
  const cost = Form.useWatch('costPrice') || 0;
  const sell = Form.useWatch('sellPrice') || 0;
  const margin = sell > 0 ? ((sell - cost) / sell) * 100 : 0;
  const labelStyle: React.CSSProperties = {
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.88)',
    display: 'block',
    marginBottom: 8,
  };
  return (
    <div style={{ marginBottom: 24 }}>
      <span style={labelStyle}>{L('Margin %')}</span>
      <span style={{ fontSize: 16, color: margin >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
        {margin.toFixed(1)}%
      </span>
    </div>
  );
};

const InventoryItemDrawer: React.FC<IProps> = ({
  open,
  editingId,
  editingDocuments,
  form,
  categoryOptions,
  itemInvoiceFile,
  previewDoc,
  saving,
  deleting,
  onClose,
  onSubmit,
  onItemInvoiceSelect,
  onItemInvoiceRemove,
  onPreviewDocument,
  onClosePreview,
  onDelete,
}) => {
  const sasQuery = useDocumentSasUrl();
  const sasSuffix = sasQuery ? `?${sasQuery.split('?')[1] ?? ''}` : '';
  const { isMobile } = useView();

  return (
  <Drawer
    title={editingId ? L('Edit Inventory Item') : L('New Inventory Item')}
    size={isMobile ? '100%' : previewDoc ? 1300 : 600}
    open={open}
    onClose={onClose}
    styles={previewDoc ? { body: { padding: 0, overflow: 'hidden' } } : undefined}
    footer={
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {editingId && onDelete && (
            <Popconfirm
              title={L('Delete this item?')}
              description={L('Make sure you don’t physically have any of this stock left before deleting. This cannot be undone.')}
              okText={L('Delete')}
              okButtonProps={{ danger: true }}
              cancelText={L('Cancel')}
              onConfirm={() => onDelete()}
              disabled={saving || deleting}
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                loading={deleting}
                disabled={saving}
              >
                {L('Delete')}
              </Button>
            </Popconfirm>
          )}
        </div>
        <Space>
          <Button onClick={onClose} disabled={saving || deleting}>{L('Cancel')}</Button>
          <Button type="primary" onClick={onSubmit} loading={saving} disabled={deleting}>{L('Save')}</Button>
        </Space>
      </div>
    }
  >
   <div style={{ display: 'flex', height: '100%' }}>
    {/* There is no room for a side-by-side pane on a phone, so the preview takes over. */}
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        minWidth: 0,
        minHeight: 0,
        ...(isMobile && previewDoc ? { display: 'none' } : {}),
        ...(previewDoc && !isMobile ? { padding: '24px 16px 24px 24px' } : {}),
      }}
    >
    <Form form={form} layout="vertical">
      <Form.Item name="name" label={L('Name')} rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="reference" label={L('Reference (garage stock code)')}>
            <Input placeholder={L('Your internal code')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="partNumber" label={L('Part number (from invoice)')}>
            <Input placeholder={L('Supplier part number')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="categoryId" label={L('Category')}>
            <Select allowClear placeholder={L('Select category')} options={categoryOptions} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="nominalCode" label={L('Nominal Code')}>
            <Input placeholder={L('e.g. NM-BODY')} maxLength={180} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="purchaseDate" label={L('Purchase date')} getValueProps={(v) => ({ value: v ? dayjsRef(v) : undefined })} normalize={(v: any) => (v ? v.toISOString() : undefined)}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item name="supplierId" label={L('Supplier')}>
            <SupplierSelect />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="supplierInvoiceNumber" label={L('Invoice Number')}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={12} md={5}>
          <Form.Item name="quantity" label={L('Quantity')} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col xs={12} md={5}>
          <Form.Item name="minStockLevel" label={L('Min Stock')}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col xs={8} md={5}>
          <Form.Item name="costPrice" label={L('Cost (£)')} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
        </Col>
        <Col xs={8} md={5}>
          <Form.Item name="sellPrice" label={L('Sale (£)')} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
        </Col>
        <Col xs={8} md={4}>
          <MarginDisplay />
        </Col>
      </Row>
      <Form.Item name="description" label={L('Description')}>
        <Input.TextArea rows={2} />
      </Form.Item>

      <style>{`
        .inv-segmented .ant-segmented-item-selected {
          background-color: #1677ff !important;
        }
        .inv-segmented .ant-segmented-item-selected .ant-segmented-item-label {
          color: #fff !important;
          font-weight: 600;
        }
      `}</style>
      <Row gutter={8} align="bottom">
        <Col>
          <Form.Item
            name="isInShop"
            label={L('Regular item')}
            getValueProps={(v) => ({ value: v === true ? 'yes' : v === false ? 'no' : 'no' })}
            normalize={(v: any) => v === 'yes'}
          >
            <Segmented
              className="inv-segmented"
              options={[
                { label: L('Regular'), value: 'yes' },
                { label: L('Not regular'), value: 'no' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col flex="auto">
          <Form.Item name="location" label={L('Location (shelf / row / bin)')}>
            <Input placeholder={L('e.g. A3-12')} />
          </Form.Item>
        </Col>
      </Row>

      {editingId && editingDocuments.length > 0 && (
        <Form.Item label={L('Attached invoices')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {editingDocuments.map((doc) => {
              // Signed per blob by the API; sasSuffix is the old container-wide token, kept only as
              // a fallback for documents stored before signing.
              const href = doc.signedUrl ?? `${doc.url}${sasSuffix}`;
              const isImage = (doc.contentType || '').toLowerCase().startsWith('image/');
              const isActive = previewDoc?.src === href;
              return (
                <div
                  key={doc.id}
                  onClick={() => onPreviewDocument({ name: doc.name, src: href, contentType: doc.contentType })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    background: isActive ? '#e6f7ff' : '#fafafa',
                    border: `1px solid ${isActive ? '#91d5ff' : '#f0f0f0'}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  {isImage ? (
                    <Image
                      src={href}
                      alt={doc.name}
                      width={48}
                      height={48}
                      preview={false}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <FileOutlined style={{ color: '#13c2c2' }} />
                  )}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span
                      style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1677ff' }}
                    >
                      {doc.name}
                    </span>
                    {doc.creationTime && (
                      <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {L('Uploaded')} {dayjs(doc.creationTime).format('DD MMM YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Form.Item>
      )}

      <Form.Item label={L('Invoice Image')}>
        <ItemInvoiceAttachment
          file={itemInvoiceFile}
          onSelect={onItemInvoiceSelect}
          onRemove={onItemInvoiceRemove}
          onPreview={
            itemInvoiceFile
              ? () =>
                  onPreviewDocument({
                    name: itemInvoiceFile.name,
                    src: itemInvoiceFile.base64,
                    contentType: itemInvoiceFile.contentType,
                  })
              : undefined
          }
        />
      </Form.Item>
    </Form>
    </div>
    {previewDoc && (
      <DocumentPreviewPane doc={previewDoc} width={isMobile ? undefined : 700} onClose={onClosePreview} />
    )}
   </div>
  </Drawer>
  );
};

export default InventoryItemDrawer;
